import { LineStringReadable } from "./line-string-readable";
import type { ReadonlyVec } from "./readonly";
import { VecLike } from "./vec-like";

export class PolygonReadable<Point extends ReadonlyVec,Points extends ReadonlyArray<Point>>{
	constructor(
		public points:Points,
	){
	}

	public [Symbol.iterator](){
		return this.points.values();
	}

	public pairs(){
		return this.points.pairs(true);
	}

	public at(i:number){
		return this.points.at(i%this.points.length);
	}

	public atIndex(index:number){
		const {points}=this;
		index%=points.length;
		if(index<0)
			index+=points.length;

		let i=Math.floor(index);
		const p=points[i].clone();
		if(i===index)
			return p;
		return p.lerp(points[(i+1)%points.length],index-i);
	}

	public get count(){
		return this.points.length;
	}

	public len(){
		let length=0;
		for(const [a,b] of this.points.pairs(true))
			length+=a.distTo(b);
		return length;
	}

	public eq(that:PolygonReadable<ReadonlyVec,ReadonlyArray<ReadonlyVec>>){
		if(this.points.length!==that.points.length)
			return false;
		for(const [i,p] of this.points.entries()){
			if(!p.eq(that.points[i]))
				return false;
		}
		return true;
	}

	public containsXY(p:VecLike):boolean{
		return PolygonReadable.containsXY(this.points,p);
	}

	public distTo(focus:VecLike){
		return LineStringReadable.distTo(this.points,true,focus);
	}

	public closestPoint(focus:VecLike){
		return LineStringReadable.closestPoint<Point>(this.points,true,focus);
	}

	public closestIndex(focus:VecLike){
		return LineStringReadable.closestIndex(this.points,true,focus);
	}


}

export namespace PolygonReadable{
	function cross(a:VecLike, b:VecLike, c:VecLike):number{
		return (b.x-a.x)*(c.y-a.y)-(c.x-a.x)*(b.y-a.y);
	}
	
	export function containsXY(points:ReadonlyArray<VecLike>, p:VecLike):boolean{
		let wn=0; // winding number
		for(let i=0;i<points.length;++i){
			const a=points[i];
			const b=points[(i+1)%points.length];
			if(a.y<=p.y){
				if(b.y>p.y && cross(a,b,p)>0)
					wn+=1;
			}else if(b.y<=p.y && cross(a,b,p)<0)
				wn-=1;
		}
		return wn!==0;
	}


}
